<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { detectTypedMode } from '@/core/sourcing/search/utils/detect-mode.util';
import type { SearchMode } from '@/core/sourcing/search/types/search-mode.type';

import JobPickerDropdown from './JobPickerDropdown.vue';

// vite-svg-loader's svgo strips viewBox from these icons, so each usage sets the
// icon's native viewBox explicitly — without it, sizing an icon below its intrinsic
// 24px (or 16x20 for the sparkle) via CSS clips the artwork instead of scaling it.
import SearchIcon from '@/assets/svg/magnifying-glass.svg?component';
import SparkleIcon from '@/assets/svg/sparkle.svg?component';
import BriefcaseIcon from '@/assets/svg/briefcase.svg?component';
import CloseIcon from '@/assets/svg/close-24.svg?component';
import ChevronDownIcon from '@/assets/svg/chevron-down.svg?component';
import { formatProjectLocation } from '@/core/shared/project/project-location.util';

type TypedSearchMode = 'natural' | 'keyword_boolean';

interface ActiveJob {
  title: string;
  location: string;
  /** One-sentence display-only summary shown in the (disabled) input while the job is active. */
  summary: string;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    activeJob?: ActiveJob | null;
    isSearching?: boolean;
    // Lets the view hide the mode chips entirely when only keyword/Boolean is
    // supported. Defaults true so the standalone component and its stories keep
    // showing the full chip set.
    showModeChips?: boolean;
  }>(),
  {
    modelValue: '',
    activeJob: null,
    isSearching: false,
    showModeChips: true,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'update:mode', mode: SearchMode): void;
  (e: 'search', payload: { query: string; mode: SearchMode }): void;
  // Fired only by the explicit clear affordance (the input's X) — never by typing or
  // erasing text — so the parent can reset results without fighting the v-model.
  (e: 'clear'): void;
  (e: 'clear-job'): void;
  (e: 'select-job', jobId: number): void;
}>();

// Illustrative placeholder prompts only. The curated example set (product-owned)
// lives in the recent-searches empty state, not the input chrome.
const ROTATING_EXAMPLES = [
  'Maintenance technician near Cincinnati',
  'CNC machinist in Houston active in the last 30 days',
  'TIG welders with AWS certifications near Madison, WI',
  'Quality inspector with aerospace background',
  'Forklift operator within 50 miles of Detroit',
];

const query = computed<string>({
  // withDefaults only substitutes the default for undefined, so coalesce here to
  // stay a string even if a consumer binds a nullable value.
  get: () => props.modelValue ?? '',
  set: (value) => emit('update:modelValue', value),
});

const inputRef = ref<HTMLInputElement | null>(null);
const focused = ref(false);

// forcedMode is deliberately component-local: it stays put through the session
// but resets to the natural default on a fresh page load (component remount).
const forcedMode = ref<TypedSearchMode | null>(null);

const hasQuery = computed(() => query.value.trim().length > 0);
const onJob = computed(() => !!props.activeJob);
const autoMode = computed<TypedSearchMode>(() => detectTypedMode(query.value));

// What the input renders: in job mode the job's one-sentence summary (display-only —
// the request is built from the job fields, never from this string, and it must not
// leak into the v-model/URL/recents); otherwise the typed query. The input is disabled
// in job mode, so the summary can never be edited back through onInput.
const displayValue = computed(() => (onJob.value ? props.activeJob?.summary ?? '' : query.value));
const hasDisplayValue = computed(() => displayValue.value.trim().length > 0);

// An active job is the unambiguous source of truth; otherwise a manual chip
// override wins; otherwise we trust auto-detection of the typed text.
const effectiveMode = computed<SearchMode>(() =>
  onJob.value ? 'job' : forcedMode.value ?? autoMode.value,
);

// Surface the resolved mode live so the view can reveal/hide filter chrome before a
// search runs — e.g. the keyword/Boolean panel appears the moment that chip activates.
// The initial value is pushed from onMounted (not an immediate watch) so we don't emit
// during setup and mutate the parent's state mid-render.
watch(effectiveMode, (mode) => emit('update:mode', mode));

const TYPED_CHIPS: { mode: TypedSearchMode; label: string; tip: string }[] = [
  { mode: 'natural', label: 'Natural language', tip: 'Describe candidates in plain language' },
  { mode: 'keyword_boolean', label: 'Keywords & Boolean', tip: 'Exact terms, AND/OR/NOT, quotes' },
];

// A chip reads as auto-detected only when nothing has been forced, no job is
// active, and there is text for the detector to act on.
function isAuto(mode: TypedSearchMode): boolean {
  return !forcedMode.value && !onJob.value && hasQuery.value && autoMode.value === mode;
}

function isForced(mode: TypedSearchMode): boolean {
  // An active job outranks a forced typed mode, so a forced chip must not keep its
  // highlight once a job takes over as the effective mode.
  return !onJob.value && forcedMode.value === mode;
}

function pickMode(mode: TypedSearchMode): void {
  if (forcedMode.value === mode) {
    forcedMode.value = null;
    return;
  }
  forcedMode.value = mode;
  // A typed-text chip and an active job are mutually exclusive search sources.
  if (props.activeJob) {
    emit('clear-job');
  }
}

const modeHint = computed(() => {
  if (onJob.value) return '';
  if (forcedMode.value) return 'Forced — click to unset';
  return hasQuery.value ? 'Auto-detected' : '';
});

function submit(): void {
  // In job mode the active job is the query, so an empty typed input is allowed.
  if ((!hasQuery.value && !onJob.value) || props.isSearching) return;
  emit('search', { query: query.value.trim(), mode: effectiveMode.value });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    submit();
  }
}

// Behaves exactly like v-model in typed mode; in job mode the input is disabled so this
// can never fire, keeping the displayed summary out of the modelValue.
function onInput(event: Event): void {
  query.value = (event.target as HTMLInputElement).value;
}

function clearQuery(): void {
  query.value = '';
  emit('clear');
  inputRef.value?.focus();
}

// The input's X while a job is active clears the job itself (the summary is not editable
// text). The parent drops the job synchronously, so on nextTick the input is re-enabled
// and can take focus for the next query (mirrors the prototype's refocus).
function clearJob(): void {
  emit('clear-job');
  void nextTick(() => inputRef.value?.focus());
}

// The "Search from a job" picker. The parent owns the job fetch + activeJob state;
// this only surfaces the chosen job id. Toggling lets a user reopen it to change jobs.
const jobPickerOpen = ref(false);

function toggleJobPicker(): void {
  jobPickerOpen.value = !jobPickerOpen.value;
}

function onPickJob(jobId: number): void {
  emit('select-job', jobId);
  jobPickerOpen.value = false;
}

const placeholderIdx = ref(0);
const placeholder = computed(() => ROTATING_EXAMPLES[placeholderIdx.value]);
let rotateTimer: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  // Push the resolved mode once the parent is mounted (see the effectiveMode watch).
  emit('update:mode', effectiveMode.value);

  rotateTimer = setInterval(() => {
    if (!hasQuery.value) {
      placeholderIdx.value = (placeholderIdx.value + 1) % ROTATING_EXAMPLES.length;
    }
  }, 3400);
});

onBeforeUnmount(() => {
  if (rotateTimer) clearInterval(rotateTimer);
});

// Lets the view's empty-state "Start search" affordance focus the input, mirroring
// the keyword-input focus on the legacy path.
defineExpose({
  focusInput() {
    inputRef.value?.focus();
  },
  // The sidebar's Apply runs the search through here rather than assembling its own payload:
  // `effectiveMode` folds in the forced-mode chip and job mode, and the guards below decide
  // when a submit is legal at all. A second construction of either would drift from this one.
  submit,
});
</script>

<template>
  <!-- The job picker panel/backdrop render inside this stacking context: keep it above the z-10 results chrome
       and the enrichment trace (z-index 9) below it (ONE-6904). Tier utilities sit on the class attr for the spec. -->
  <header class="smart-search-header relative z-20">
    <div class="hdr-top">
      <h1 class="hdr-title">Talent search</h1>
    </div>

    <!-- Active-job banner: when a job is supplied the banner renders and its X
         clears the job context. -->
    <div v-if="activeJob" class="job-banner" data-test="smart-search-job-banner">
      <BriefcaseIcon class="banner-icon" viewBox="0 0 24 24" />
      <span class="jb-label">Searching based on:</span>
      <span class="jb-title">{{ activeJob.title }}</span>
      <span class="jb-meta">{{ formatProjectLocation(activeJob.location) }}</span>
      <button
        class="jb-x"
        aria-label="Clear job context"
        data-test="smart-search-clear-job"
        @click="emit('clear-job')"
      >
        <CloseIcon class="icon-xs" viewBox="0 0 24 24" />
      </button>
    </div>

    <div class="search-row" :class="{ focused }">
      <span class="search-icon">
        <SparkleIcon v-if="hasDisplayValue" class="icon-md" viewBox="0 0 16 20" />
        <SearchIcon v-else class="icon-md" viewBox="0 0 24 24" />
      </span>
      <input
        ref="inputRef"
        class="search-input"
        :value="displayValue"
        :placeholder="onJob ? 'Searching from the selected job' : placeholder"
        :disabled="onJob"
        aria-label="Search candidates"
        data-test="smart-search-input"
        @input="onInput"
        @focus="focused = true"
        @blur="focused = false"
        @keydown="onKeydown"
      />
      <div class="search-actions">
        <!-- The X lives outside the input, so it stays clickable while the input is
             disabled in job mode — where it clears the job, not the (empty) text. -->
        <button
          v-if="hasQuery || onJob"
          type="button"
          class="search-clear"
          :aria-label="onJob ? 'Clear job search' : 'Clear search'"
          :title="onJob ? 'Clear job search' : 'Clear'"
          data-test="smart-search-clear"
          @click="onJob ? clearJob() : clearQuery()"
        >
          <CloseIcon class="icon-xs" viewBox="0 0 24 24" />
        </button>
        <button
          type="button"
          class="btn-search"
          :disabled="(!hasQuery && !onJob) || isSearching"
          data-test="smart-search-submit"
          @click="submit"
        >
          {{ isSearching ? 'Searching…' : 'Search' }}
        </button>
      </div>
    </div>

    <!-- Below md the chips scroll on one line; the outer wrap (not the scroller) anchors
         the job picker panel so the scroller's overflow can't clip it. -->
    <div class="mode-chips-wrap">
      <div class="mode-chips">
        <template v-if="showModeChips">
          <button
            v-for="chip in TYPED_CHIPS"
            :key="chip.mode"
            type="button"
            class="mode-chip"
            :class="{ 'is-auto': isAuto(chip.mode), 'is-forced': isForced(chip.mode) }"
            :title="chip.tip"
            :aria-pressed="isForced(chip.mode) || isAuto(chip.mode)"
            :data-test="`smart-search-mode-${chip.mode}`"
            @click="pickMode(chip.mode)"
          >
            <SparkleIcon v-if="chip.mode === 'natural'" class="icon-sm" viewBox="0 0 16 20" />
            <span v-else class="chip-hash">#</span>
            {{ chip.label }}
            <!-- Decorative "click to unset" cue on a forced chip; the chip's own click
               already toggles the mode off, so this stays non-interactive. -->
            <span v-if="isForced(chip.mode)" class="mode-chip-x" aria-hidden="true">
              <CloseIcon class="icon-2xs" viewBox="0 0 24 24" />
            </span>
          </button>
        </template>

        <!-- Clicking "Search from a job" opens the active-jobs picker; with a job active it
           reopens so the user can switch to a different job. -->
        <div class="job-chip-wrap">
          <button
            type="button"
            class="mode-chip is-job"
            :class="{ 'is-forced': onJob }"
            :aria-expanded="jobPickerOpen"
            title="Start a search from one of your jobs"
            data-test="smart-search-mode-job"
            @click="toggleJobPicker"
          >
            <BriefcaseIcon class="icon-sm" viewBox="0 0 24 24" />
            Search from a job
            <ChevronDownIcon class="icon-2xs opacity-70" viewBox="0 0 24 24" />
          </button>
          <JobPickerDropdown
            v-if="jobPickerOpen"
            @select="onPickJob"
            @close="jobPickerOpen = false"
          />
        </div>

        <span
          v-if="showModeChips && modeHint"
          class="mode-hint"
          data-test="smart-search-mode-hint"
          >{{ modeHint }}</span
        >
      </div>
    </div>
  </header>
</template>

<style scoped lang="postcss">
.smart-search-header {
  @apply bg-shade-880 px-4 py-5 sm:px-8;
  border-bottom: 1px solid #000;
}

.hdr-top {
  @apply mb-5 flex items-center justify-between;
}

.hdr-title {
  @apply font-serif text-lg font-black leading-6 text-tint-40;
}

/* Active-job banner keeps the green job-affordance treatment, distinct from the
   purple typed-mode accent below. */
.job-banner {
  @apply mb-3 flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-[10px] px-3.5 py-2.5 text-sm;
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.35);
  color: #a9e7bf;
}
.banner-icon {
  @apply h-3.5 w-3.5 shrink-0 text-primary-300;
}
.jb-label {
  @apply text-[12.5px] font-semibold text-primary-300;
}
.jb-title {
  @apply text-[13.5px] font-semibold text-white;
}
.jb-meta {
  @apply text-[12.5px] text-shade-600;
}
.jb-x {
  @apply ml-auto inline-flex shrink-0 rounded p-1 text-shade-600 transition-colors;
}
.jb-x:hover {
  @apply text-white;
  background: rgba(255, 255, 255, 0.06);
}

.search-row {
  @apply flex items-stretch overflow-hidden rounded-[14px];
  background: #141a2e;
  border: 1px solid #1e2642;
  transition:
    border-color 180ms ease,
    box-shadow 180ms ease,
    background 180ms ease;
}
.search-row.focused {
  background: #1a2140;
  border-color: rgba(108, 64, 229, 0.5);
  box-shadow: 0 0 0 4px rgba(108, 64, 229, 0.12);
}
.search-icon {
  @apply flex items-center justify-center pl-[18px] text-shade-600;
}
.search-row.focused .search-icon {
  @apply text-highlight-400;
}
.search-input {
  @apply min-w-0 flex-1 border-none bg-transparent px-3 py-[18px] text-[15px] text-tint-40 outline-none;
  caret-color: #6c40e5;
}
.search-input::placeholder {
  @apply text-shade-600;
}
.search-input:disabled {
  @apply cursor-not-allowed text-shade-600;
}

.search-actions {
  @apply flex items-center gap-2 py-2 pl-0 pr-2.5;
}
.search-clear {
  @apply inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-shade-600 transition-colors;
}
.search-clear:hover {
  @apply text-white;
  background: rgba(255, 255, 255, 0.08);
}

.btn-search {
  @apply inline-flex shrink-0 items-center rounded-[10px] bg-highlight-500 px-5 py-2.5 transition-colors;
  @apply text-[13.5px] font-bold tracking-[0.01em] text-white;
}
.btn-search:hover:not(:disabled) {
  @apply bg-highlight-600;
}
.btn-search:active:not(:disabled) {
  transform: translateY(1px);
}
.btn-search:disabled {
  @apply cursor-not-allowed text-shade-600;
  background: #23294a;
}

.mode-chips-wrap {
  @apply relative;
}
.mode-chips {
  @apply mt-3 flex flex-wrap items-center gap-2;
}
.job-chip-wrap {
  @apply relative;
}
@media (max-width: 767px) {
  /* Bleeds to the header edge (cancels its px-4 / sm:px-8) so chips scroll off-screen;
     py-1 (offset by the margins) leaves room for focus outlines the scroller would clip. */
  .mode-chips {
    @apply -mx-4 -mb-1 mt-2 flex-nowrap overflow-x-auto px-4 py-1 sm:-mx-8 sm:px-8;
    scrollbar-width: none;
  }
  .mode-chips::-webkit-scrollbar {
    display: none;
  }
  .job-chip-wrap {
    @apply static;
  }
}
.mode-chip {
  @apply inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 transition-colors;
  @apply text-xs font-semibold text-shade-600;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid #1e2642;
}
.mode-chip:hover:not(:disabled) {
  @apply text-white;
  border-color: #2a3356;
  background: rgba(255, 255, 255, 0.05);
}
.mode-chip.is-auto {
  @apply text-highlight-100;
  background: rgba(108, 64, 229, 0.12);
  border-color: rgba(108, 64, 229, 0.45);
}
.mode-chip.is-forced {
  @apply bg-highlight-500 font-bold text-white;
  border-color: #6c40e5;
}
.mode-chip:disabled {
  @apply cursor-not-allowed opacity-50;
}
.chip-hash {
  @apply text-xs font-extrabold;
}
.mode-chip-x {
  @apply ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full;
  background: rgba(0, 0, 0, 0.18);
}
.mode-chip-x:hover {
  background: rgba(0, 0, 0, 0.32);
}
.mode-hint {
  @apply ml-1 shrink-0 whitespace-nowrap text-[11.5px] text-shade-600;
}

.icon-md {
  @apply h-[18px] w-[18px];
}
.icon-sm {
  @apply h-3 w-3;
}
.icon-xs {
  @apply h-3.5 w-3.5;
}
.icon-2xs {
  @apply h-2.5 w-2.5;
}
</style>
